"use client";

import { useAuth } from "@/contexts/auth-and-permissions-context";
import LoadingSpinner from "@/components/loading-spinner";
import { useEffect } from "react";
import { useRouter, usePathname } from "next/navigation";
import { routeUtils } from "@/lib/constants/routes";

interface GlobalLayoutWrapperProps {
  children: React.ReactNode;
}

export default function GlobalLayoutWrapper({
  children,
}: GlobalLayoutWrapperProps) {
  const { isLoading, isAuthenticated } = useAuth();
  const router = useRouter();
  const pathname = usePathname();

  // Use centralized route utilities
  const isPublicRoute = routeUtils.isPublicRoute(pathname);
  const isPortalPage = routeUtils.isPortalPage(pathname);
  const isMinimalLayout = routeUtils.isMinimalLayout(pathname);

  useEffect(() => {
    // Only redirect for non-portal, non-public pages
    if (!isLoading && !isAuthenticated && !isPublicRoute && !isPortalPage) {
      router.push("/login");
    }

    // Redirect authenticated users away from login page
    if (!isLoading && isAuthenticated && pathname === "/login") {
      // Redirect to dashboard - the middleware will handle role-based routing
      router.push("/superadmin/dashboard"); // Default redirect, will be handled by auth logic
    }
  }, [
    isLoading,
    isAuthenticated,
    isPublicRoute,
    isPortalPage,
    router,
    pathname,
  ]);

  // Allow public routes to render immediately
  if (isPublicRoute) {
    return <>{children}</>;
  }

  // Routes that need minimal layout (no header/footer)
  if (isMinimalLayout) {
    return <>{children}</>;
  }

  // Don't show loading for portal pages (they handle their own auth)
  if (isPortalPage) {
    return <>{children}</>;
  }

  // Show loading spinner during initial auth check for protected pages
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading..." />
      </div>
    );
  }

  // Redirect unauthenticated users to login
  if (!isAuthenticated) {
    return null; // Will redirect to login
  }

  return <>{children}</>;
}
